import {
  Card,
  Box as Flex,
  Link,
  Typography as Text,
  makeStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import { useAuth } from '../../behaviors/auth-factory';
import { useSupportMessenger } from '../../hooks/useSupportMessenger';

const useStylesMui = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  fillViewport: {
    minHeight: '60vh',
  },
  description: {
    textAlign: 'center',
    maxWidth: '520px',
  },
  cards: {
    maxWidth: '720px',
  },
  card: {
    flex: 1,
    padding: 'var(--encore-spacing-base)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--encore-spacing-tighter-2)',
  },
  link: {
    display: 'inline',
    textDecoration: 'underline',
  },
}));

const COPY_BY_VARIANT_MUI = {
  page: {
    title: 'Page not found',
    description:
      "Oops! Looks like you've ventured off the beaten path. The page you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
  resource: {
    title: 'Resource not found',
    description:
      "Oops! The resource you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
} as const;

export type NotFoundContentPropsMui = {
  variant?: 'page' | 'resource';
  fillViewport?: boolean;
};

/** @deprecated Legacy MUI copy — migrate consumers to the Encore NotFoundContent and delete. */
export const NotFoundContentMui = ({
  variant = 'page',
  fillViewport = true,
}: NotFoundContentPropsMui = {}) => {
  const classes = useStylesMui();
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const { title, description } = COPY_BY_VARIANT_MUI[variant];

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <div
      className={classNames(
        classes.container,
        fillViewport && classes.fillViewport,
      )}
    >
      <Flex
        display="flex"
        justifyContent="center"
        flexDirection="column"
        style={{
          gap: 'var(--encore-spacing-looser)',
        }}
      >
        <Flex display="flex" flexDirection="column" alignItems="center">
          <Text variant="subtitle2" color="textSecondary">
            Error 404
          </Text>
          <Text variant="h3" component="h2">
            {title}
          </Text>
        </Flex>
        <Flex display="flex" flexDirection="column" mb={4} alignItems="center">
          <Text variant="body1" component="p" className={classes.description}>
            {description}
          </Text>
        </Flex>
        <Flex
          display="flex"
          flexDirection="row"
          className={classes.cards}
          style={{
            gap: 'var(--encore-spacing-looser)',
          }}
        >
          <Card className={classes.card}>
            <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
              Double-check the URL
            </Text>
            <Text variant="body1" color="textSecondary" component="p">
              Sometimes a simple typo is the culprit
            </Text>
          </Card>
          <Card className={classes.card}>
            <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
              Start fresh
            </Text>
            <Text variant="body1" color="textSecondary" component="p">
              <Link href="/" className={classes.link}>
                Go back to the homepage
              </Link>{' '}
              and find what you need
            </Text>
          </Card>
          <Card className={classes.card}>
            <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
              Need help?
            </Text>
            <Text variant="body1" color="textSecondary" component="p">
              If you think this is an error, please{' '}
              <Link
                className={classes.link}
                href="#"
                onClick={e => {
                  e.preventDefault();
                  handleContactSupport();
                }}
              >
                contact our support team
              </Link>
            </Text>
          </Card>
        </Flex>
      </Flex>
    </div>
  );
};
